import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Crosshair, Cpu, Loader2, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader } from "./SovereignPace";

const BRANCHES = ["", "ARMY", "NAVY", "AIR FORCE", "MARINES", "SPACE FORCE", "COAST GUARD"];

export const MosCrosswalk = () => {
    const [code, setCode] = useState("11B");
    const [branch, setBranch] = useState("ARMY");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [showCatalog, setShowCatalog] = useState(false);

    useEffect(() => {
        endpoints.mosCatalog().then((r) => setCatalog(r.data.entries || [])).catch(() => {});
    }, []);

    const runLookup = async (e) => {
        e?.preventDefault();
        if (!code.trim()) {
            toast.error("Provide a MOS / job code");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const res = await endpoints.mosLookup({ code: code.trim(), branch: branch || null });
            setResult(res.data);
            toast.success(`CROSSWALK COMPLETE · ${res.data.code}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Crosswalk failed");
        } finally {
            setLoading(false);
        }
    };

    const pickFromCatalog = (m) => {
        setCode(m.code);
        setBranch(m.branch);
        setShowCatalog(false);
    };

    return (
        <section
            id="mos-crosswalk"
            data-testid="mos-crosswalk"
            className="relative py-20 border-b border-zinc-900 grid-bg"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-8">
                <SectionHeader
                    eyebrow="MODULE · 001"
                    title="MOS-to-Armada Crosswalk"
                    subtitle="AI-mapped DoD job codes to EIGOO business hulls. Army, Navy, Air Force, Marines, Space Force, Coast Guard."
                />

                <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-10">
                    {/* Input panel */}
                    <div className="col-span-12 lg:col-span-5 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative corner-bracket">
                        <div className="flex items-center gap-2">
                            <Cpu className="w-4 h-4 text-gold-300" />
                            <span className="label-tactical">EXOSKELETON · INPUT</span>
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-tight text-white mt-2">
                            Run Crosswalk
                        </h3>

                        <form onSubmit={runLookup} className="mt-6 space-y-4">
                            <div>
                                <label className="label-tactical">MOS / RATING / AFSC</label>
                                <input
                                    data-testid="mos-code-input"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="e.g. 11B, IT, 1N4, 0651"
                                    className="mt-2 w-full bg-zinc-900 border border-zinc-800 focus:border-gold-300 px-4 py-3 text-white font-mono uppercase tracking-wider outline-none transition-colors"
                                />
                            </div>
                            <div>
                                <label className="label-tactical">BRANCH</label>
                                <select
                                    data-testid="mos-branch-select"
                                    value={branch}
                                    onChange={(e) => setBranch(e.target.value)}
                                    className="mt-2 w-full bg-zinc-900 border border-zinc-800 focus:border-gold-300 px-4 py-3 text-white uppercase tracking-wider outline-none appearance-none cursor-pointer"
                                >
                                    {BRANCHES.map((b) => (
                                        <option key={b || "any"} value={b}>
                                            {b || "ANY BRANCH"}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                data-testid="mos-lookup-btn"
                                disabled={loading}
                                type="submit"
                                className="group w-full bg-gold-300 hover:bg-gold-200 disabled:opacity-60 text-black px-6 py-4 uppercase tracking-[0.22em] text-xs font-black transition-all flex items-center justify-center gap-3"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Mapping...
                                    </>
                                ) : (
                                    <>
                                        <Crosshair className="w-4 h-4" />
                                        Execute Crosswalk
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="mt-6">
                            <button
                                data-testid="catalog-toggle"
                                onClick={() => setShowCatalog(!showCatalog)}
                                className="w-full flex items-center justify-between border-t border-zinc-800/80 pt-4 text-zinc-400 hover:text-gold-300 transition-colors"
                            >
                                <span className="label-tactical">CATALOG · {catalog.length} ENTRIES</span>
                                <ChevronDown
                                    className={`w-4 h-4 transition-transform ${showCatalog ? "rotate-180" : ""}`}
                                />
                            </button>
                            {showCatalog && (
                                <div className="mt-3 max-h-64 overflow-y-auto border border-zinc-800/80">
                                    {catalog.map((m, i) => (
                                        <button
                                            key={`${m.branch}-${m.code}-${i}`}
                                            data-testid={`catalog-${i}`}
                                            onClick={() => pickFromCatalog(m)}
                                            className="w-full text-left px-3 py-2 border-b last:border-b-0 border-zinc-900 hover:bg-zinc-900/60 hover:text-gold-300 flex items-center gap-3 text-xs uppercase"
                                        >
                                            <span className="font-mono text-gold-300 w-12">{m.code}</span>
                                            <span className="text-zinc-500 w-20">{m.branch}</span>
                                            <span className="text-zinc-300 truncate">{m.title}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Result panel */}
                    <div
                        data-testid="mos-result"
                        className="col-span-12 lg:col-span-7 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative min-h-[400px]"
                    >
                        <div className="flex items-center gap-2">
                            <span className="label-tactical">STRATEGIC SIGNAL · OUTPUT</span>
                        </div>

                        {!result && !loading && (
                            <EmptyState />
                        )}

                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <Loader2 className="w-8 h-8 text-gold-300 animate-spin mx-auto" />
                                    <div className="label-tactical mt-4">AI MAPPING · IN PROGRESS</div>
                                </div>
                            </div>
                        )}

                        {result && !loading && (
                            <div className="mt-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <ResultBlock label="Code" value={result.code} mono />
                                    <ResultBlock label="Branch" value={result.branch} />
                                    <ResultBlock label="TAP Priority" value={result.tap_priority} priority />
                                </div>

                                <div>
                                    <div className="label-tactical">SPECIALTY</div>
                                    <div className="text-2xl font-black uppercase tracking-tight text-white mt-1">
                                        {result.title}
                                    </div>
                                </div>

                                <div className="border border-gold-300/30 bg-gold-300/5 p-5">
                                    <div className="flex items-center gap-3">
                                        <ArrowRight className="w-5 h-5 text-gold-300" />
                                        <div className="label-tactical">ARMADA HULL</div>
                                    </div>
                                    <div className="text-2xl font-black uppercase tracking-tight text-gold-300 mt-2">
                                        {result.armada_hull}
                                    </div>
                                    <div className="text-zinc-300 text-sm mt-2">
                                        Civilian Track: <span className="text-white">{result.civilian_track}</span>
                                    </div>
                                </div>

                                {result.ai_summary && (
                                    <div className="border-l-2 border-gold-300 pl-4">
                                        <div className="label-tactical">AI BRIEFING</div>
                                        <p className="text-zinc-300 text-base mt-2 leading-relaxed">
                                            {result.ai_summary}
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="border border-zinc-800/80 p-4">
                                        <div className="label-tactical">TRANSFERABLE SKILLS</div>
                                        <ul className="mt-3 space-y-1">
                                            {(result.transferable_skills || []).map((s, i) => (
                                                <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                                                    <span className="text-gold-300 mt-1">▸</span>
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="border border-zinc-800/80 p-4">
                                        <div className="label-tactical">NEXT STEPS</div>
                                        <ol className="mt-3 space-y-1">
                                            {(result.next_steps || []).map((s, i) => (
                                                <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                                                    <span className="text-gold-300 mt-0.5 font-mono">{i + 1}.</span>
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>

                                <div className="border-t border-zinc-800/80 pt-4">
                                    <div className="flex items-center justify-between">
                                        <div className="label-tactical">EXOSKELETON ÷ STRATEGIC RATIO</div>
                                        <div className="badge-tier text-gold-300">
                                            {result.automation_score}% AUTO · {100 - result.automation_score}% HUMAN
                                        </div>
                                    </div>
                                    <div className="mt-2 flex h-2 border border-zinc-800">
                                        <div
                                            className="bg-gold-300"
                                            style={{ width: `${result.automation_score}%` }}
                                        />
                                        <div
                                            className="bg-zinc-700"
                                            style={{ width: `${100 - result.automation_score}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const ResultBlock = ({ label, value, mono, priority }) => (
    <div className="border border-zinc-800/80 px-4 py-3">
        <div className="badge-tier text-zinc-500">{label.toUpperCase()}</div>
        <div
            className={`text-xl font-black mt-1 ${
                priority
                    ? value === "HIGH"
                        ? "text-gold-300"
                        : value === "MEDIUM"
                        ? "text-amber-400"
                        : "text-zinc-400"
                    : "text-white"
            } ${mono ? "font-mono" : "uppercase tracking-tight"}`}
        >
            {value}
        </div>
    </div>
);

const EmptyState = () => (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center max-w-md px-6">
            <div className="inline-flex items-center justify-center w-14 h-14 border border-zinc-800 mb-4">
                <Crosshair className="w-6 h-6 text-zinc-600" />
            </div>
            <div className="label-tactical">AWAITING INPUT</div>
            <p className="text-zinc-500 text-sm mt-2">
                Enter a MOS / Rating / AFSC code on the left to execute the crosswalk and receive
                Armada hull alignment.
            </p>
        </div>
    </div>
);

export default MosCrosswalk;
